import Header from '@/components/feature/Header';
import Footer from '@/components/feature/Footer';
import CTABanner from '@/components/feature/CTABanner';
import SEOHead from '@/components/feature/SEOHead';
import { useState } from 'react';
import { Link } from 'react-router-dom';

const faqData = [
  { q: 'Peut-on remplacer une seule lame de volet roulant ?', a: 'Oui, il est tout à fait possible de remplacer une ou plusieurs lames sans changer le tablier complet. Nos techniciens identifient le modèle exact de vos lames (PVC ou aluminium, dimensions, couleur) et procèdent au remplacement des seules lames endommagées.' },
  { q: 'Mes lames de volet roulant sont jaunies, que faire ?', a: 'Le jaunissement est un phénomène naturel pour les lames PVC exposées au soleil. Si l\'aspect esthétique vous gêne, nous pouvons remplacer les lames concernées. Si le jaunissement est généralisé, le remplacement complet du tablier peut être une meilleure solution à long terme.' },
];

export default function ChangerLame() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const faqJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqData.map((item) => ({
      '@type': 'Question', name: item.q,
      acceptedAnswer: { '@type': 'Answer', text: item.a },
    })),
  };

  return (
    <>
      <SEOHead
        title="Remplacement de lames de volet roulant à Lyon | DFPRO"
        description="Lames de volet roulant cassées ou abîmées à Lyon ? Remplacement PVC ou alu à l'unité ou du tablier complet. Devis gratuit, intervention rapide. 09 80 80 36 84."
        canonicalUrl="https://depanfenetres-lyon.fr/changer-lame-volet-roulant-lyon/"
        jsonLd={faqJsonLd}
      />
      <Header />
      <section className="w-full">
        <img src="/images/dfpro-volet-lame-2026.jpg" alt="Remplacement de lames de volet roulant à Lyon" className="w-full h-[200px] md:h-[300px] object-cover object-center" loading="lazy" />
      </section>


      <main className="pt-20">
        <section className="w-full bg-background-100 py-12 md:py-16">
          <div className="max-w-5xl mx-auto px-4 md:px-6">
            <nav className="flex items-center gap-2 text-xs text-secondary-400 mb-6 flex-wrap">
              <Link to="/" className="hover:text-primary-500 transition-colors">Accueil</Link>
              <i className="ri-arrow-right-s-line w-3 h-3 flex items-center justify-center"></i>
              <Link to="/reparation-volet-roulant-lyon/" className="hover:text-primary-500 transition-colors">Volet roulant</Link>
              <i className="ri-arrow-right-s-line w-3 h-3 flex items-center justify-center"></i>
              <span className="text-secondary-500">Remplacement de lames</span>
            </nav>
            <h1 className="font-heading font-bold text-2xl md:text-4xl text-foreground-800">Remplacement de lames de volet roulant à Lyon</h1>
            <p className="mt-4 text-sm md:text-lg text-secondary-500 max-w-3xl leading-relaxed">
              Lames de volet roulant cassées, tordues ou abîmées ? Nos techniciens remplacent vos lames à l'unité ou le tablier complet à Lyon et dans la métropole.
            </p>
          </div>
        </section>

        <section className="w-full bg-background-50 py-12 md:py-16">
          <div className="max-w-5xl mx-auto px-4 md:px-6">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
              <div className="lg:col-span-2 space-y-8">
                <div>
                  <h2 className="font-heading font-semibold text-lg md:text-xl text-foreground-800">Quand faut-il changer les lames de son volet roulant ?</h2>
                  <p className="mt-3 text-sm text-secondary-500 leading-relaxed">
                    Les lames sont l'élément visible du volet roulant, et elles subissent de nombreuses agressions au fil du temps : chocs (grêle, ballon, objet tombé), déformation due à la chaleur, usure des clips de liaison, ou tout simplement le vieillissement naturel du PVC qui jaunit et devient cassant.
                  </p>
                  <p className="mt-3 text-sm text-secondary-500 leading-relaxed">
                    Une lame cassée n'est pas seulement un problème esthétique. Elle fragilise l'ensemble du tablier : les lames adjacentes ne sont plus correctement maintenues, ce qui peut entraîner leur sortie du rail et le blocage complet du volet. Il est donc important de remplacer rapidement les lames endommagées.
                  </p>
                  <p className="mt-3 text-sm text-secondary-500 leading-relaxed">
                    Nos techniciens se déplacent avec un échantillonnage de lames pour identifier précisément le modèle compatible avec votre volet (PVC ou aluminium, dimensions, couleur). Nous pouvons remplacer une seule lame, plusieurs lames, ou le tablier complet selon l'état général de votre volet.
                  </p>
                </div>

                <div>
                  <h2 className="font-heading font-semibold text-lg md:text-xl text-foreground-800">Lames PVC ou aluminium : quel choix ?</h2>
                  <p className="mt-3 text-sm text-secondary-500 leading-relaxed">
                    Si vous devez remplacer le tablier complet, vous avez le choix entre deux matériaux principaux. Les lames PVC sont les plus courantes : économiques, isolantes, disponibles dans de nombreux coloris. Elles peuvent cependant jaunir avec le temps sous l'effet des UV. Les lames aluminium sont plus résistantes aux chocs et aux intempéries, ne jaunissent pas, mais sont plus onéreuses. Elles offrent également une meilleure isolation thermique grâce à la mousse injectée à l'intérieur des lames.
                  </p>
                </div>
              </div>

              <aside className="space-y-6">
                <div className="bg-background-100 rounded-lg p-5 sticky top-24">
                  <h3 className="font-heading font-semibold text-sm text-foreground-800 mb-4">Services connexes</h3>
                  <nav className="space-y-1">
                    <Link to="/volet-roulant-bloque-lyon/" className="block text-sm text-secondary-500 hover:text-primary-500 hover:bg-primary-50 rounded-md px-3 py-2 transition-colors">Volet bloqué</Link>
                    <Link to="/volet-roulant-ne-remonte-plus-lyon/" className="block text-sm text-secondary-500 hover:text-primary-500 hover:bg-primary-50 rounded-md px-3 py-2 transition-colors">Volet qui ne remonte plus</Link>
                    <Link to="/reparation-volet-roulant-lyon/" className="block text-sm text-primary-500 hover:text-primary-600 hover:bg-primary-50 rounded-md px-3 py-2 transition-colors font-medium">← Tous nos services</Link>
                  </nav>
                </div>
              </aside>
            </div>
          </div>
        </section>

        <section className="w-full bg-background-100 py-12 md:py-16">
          <div className="max-w-3xl mx-auto px-4 md:px-6">
            <h2 className="font-heading font-semibold text-lg md:text-xl text-foreground-800 text-center mb-8">Questions fréquentes — remplacement de lames</h2>
            <div className="space-y-3">
              {faqData.map((item, idx) => (
                <div key={idx} className="bg-background-50 rounded-lg border border-background-200 overflow-hidden">
                  <button onClick={() => setOpenFaq(openFaq === idx ? null : idx)} className="w-full flex items-center justify-between px-5 py-4 text-left cursor-pointer">
                    <span className="font-heading font-semibold text-sm text-foreground-800 pr-4">{item.q}</span>
                    <span className={`flex-shrink-0 transition-transform duration-200 ${openFaq === idx ? 'rotate-180' : ''}`}>
                      <i className="ri-arrow-down-s-line text-secondary-400 w-5 h-5 flex items-center justify-center"></i>
                    </span>
                  </button>
                  {openFaq === idx && <div className="px-5 pb-4"><p className="text-sm text-secondary-500 leading-relaxed">{item.a}</p></div>}
                </div>
              ))}
            </div>
          </div>
        </section>

        <CTABanner title="Lames de volet roulant abîmées à Lyon ?" subtitle="Remplacement PVC ou alu. Devis gratuit, intervention rapide." />
      </main>
      <Footer />
    </>
  );
}